export interface CommentaryPoint {
  heading: string;
  body: string;
}

export interface CommentaryTab {
  id: string;
  label: string;
  icon: string;
  intro: string;
  points: CommentaryPoint[];
}

export const commentaryTabs: CommentaryTab[] = [
  {
    id: "theme",
    label: "主题解读",
    icon: "ri-compass-3-line",
    intro:
      "把它当成一部纯粹的喜剧会看漏很多东西。影片真正的表达，藏在笑声的缝隙里——一个年轻人如何在欲望与情感之间，慢慢弄清楚自己是谁。",
    points: [
      {
        heading: "黑色幽默只是外壳",
        body: "影片用大量夸张、调侃的桥段制造笑点，但这些笑点大多指向同一个核心：当一个人不知道自己真正要什么的时候，所有看似主动的选择，其实都是被推着走的。笑过之后，留下的是空白感。",
      },
      {
        heading: "“性与爱”的边界追问",
        body: "全片反复在“身体的关系”与“情感的关系”之间来回摆动。影片没有给出标准答案，而是把这种摇摆本身当作主题——承认困惑，比假装想通了更诚实。",
      },
      {
        heading: "成长的代价",
        body: "故事的底色是一段成长。向西付出代价的方式，是他必须亲手经历那些荒唐，才能真正理解当初留在他身边的人有多珍贵。这种“后知后觉”，是很多观众共情的地方。",
      },
    ],
  },
  {
    id: "arc",
    label: "人物弧光",
    icon: "ri-user-star-line",
    intro:
      "向西这个角色之所以立得住，是因为他从头到尾都在变化，而且每一次变化都有具体的触发点，而不是编剧随手的转折。",
    points: [
      {
        heading: "从被动到直面",
        body: "早期的向西习惯被环境推动：同学怎么看他、学长怎么说、朋友怎么带，他就怎么做。真正的转折发生在他开始追问“这真的是我要的吗”，从那一刻起，他才第一次成为自己故事的主角。",
      },
      {
        heading: "好友群像的对照作用",
        body: "王静的洒脱、占士的行动力，都是向西所欠缺的。这些配角并不是陪衬，而是用来照出主角性格缺口的镜子。向西对他们的依赖与挣脱，构成了他成长的两条腿。",
      },
      {
        heading: "开放式收束的意义",
        body: "影片最终没有给向西一个明确的结局，也没有宣判对错。这种留白是刻意的——成长本来就不是一个可以被盖章的结论，它更像一个持续进行的状态。",
      },
    ],
  },
  {
    id: "era",
    label: "时代与改编",
    icon: "ri-history-line",
    intro:
      "要理解这部片子，得先理解它从哪里来：一段网络连载的文字，被搬上了大银幕，也顺势带上了那个年代港产片的印记。",
    points: [
      {
        heading: "从网络小说到电影",
        body: "影片改编自网络连载小说，原著以第一人称、口语化的方式记录一个年轻人的经历与心绪。电影保留了大量旁白与自述感，让观众像是在听一个人讲述自己的故事。",
      },
      {
        heading: "港产喜剧的类型底色",
        body: "影片延续了港产喜剧惯用的市井幽默与快节奏对话，同时把成人题材处理得更直白。这种类型化表达既是它的辨识度，也是它能引发讨论的原因之一。",
      },
      {
        heading: "被讨论的社会切片",
        body: "片中的“北上”旅程，本身也是那个时期被频繁谈论的社会现象。影片借一个人的旅途，折射出一代都市青年在情感、身份与欲望之间的普遍焦虑。",
      },
    ],
  },
];

export interface Review {
  id: string;
  title: string;
  author: string;
  stance: "推荐" | "中立" | "保留";
  score: string;
  quote: string;
  body: string;
}

export const reviews: Review[] = [
  {
    id: "r1",
    title: "一部被低估的成长片",
    author: "影迷笔记",
    stance: "推荐",
    score: "8.0",
    quote: "笑点很多，但真正让人记住的是它收尾时的克制。",
    body: "如果只把它当喜剧看，会觉得后半段突然安静下来有点突兀。但恰恰是这份安静，让它从一部类型片变成了一个有重量的故事。主角的困惑投射了很多人的青春，值得耐心看完。",
  },
  {
    id: "r2",
    title: "热闹有余，深度可以更足",
    author: "周末观影",
    stance: "中立",
    score: "6.5",
    quote: "题材本身很有空间，可惜中段节奏偏散。",
    body: "影片在旅途段落里塞进了大量见闻，热闹是热闹，但有几处推进略显重复，削弱了主线情绪。如果能更聚焦主角的内心转折，整体会更紧实。作为消遣片完全合格。",
  },
  {
    id: "r3",
    title: "类型片的一次大胆尝试",
    author: "老港片档案",
    stance: "推荐",
    score: "7.5",
    quote: "把成人题材拍出成长的苦涩，这个平衡不好拿。",
    body: "在同类题材里，它难得地没有把人物简化成标签。导演身兼主演，让整部片带着一种自嘲式的坦诚。它不完美，但足够有作者气。",
  },
  {
    id: "r4",
    title: "表达直白，受众需谨慎",
    author: "观影指南",
    stance: "保留",
    score: "5.5",
    quote: "题材和尺度决定了它并不适合所有观众。",
    body: "影片的表达方式相当直接，部分桥段对观众而言可能不太舒适。如果你是冲着轻松的都市喜剧来的，需要先做好心理准备。就电影本身而言，它的诚意是能看见的。",
  },
];

export interface ClipTag {
  id: string;
  label: string;
}

export const clipTags: ClipTag[] = [
  { id: "all", label: "全部片段" },
  { id: "grow", label: "成长启蒙" },
  { id: "overseas", label: "异乡求学" },
  { id: "journey", label: "北上旅途" },
  { id: "reflect", label: "回望反思" },
];

export interface Clip {
  id: string;
  tag: string;
  tagLabel: string;
  title: string;
  scene: string;
  desc: string;
  image: string;
}

export const clips: Clip[] = [
  {
    id: "c1",
    tag: "grow",
    tagLabel: "成长启蒙",
    title: "校园里的第一次攀谈",
    scene: "香港 · 少年时期",
    desc: "一段青涩又笨拙的对话，把少年向西对两性关系的无知与好奇一次性摊开。笑点在台词的错位，情绪却落在“没人教过他”的空白上。",
    image:
      "https://readdy.ai/api/search-image?query=A%20nostalgic%20Hong%20Kong%20school%20corridor%20in%20warm%20afternoon%20light%2C%20empty%20classroom%20chairs%20and%20green%20chalkboard%2C%20stylized%20cinematic%20film%20still%2C%20amber%20and%20crimson%20color%20grade%2C%20soft%20grain%20retro%20aesthetic&width=900&height=600&seq=clip-school-01&orientation=landscape",
  },
  {
    id: "c2",
    tag: "grow",
    tagLabel: "成长启蒙",
    title: "学长的一堂“课”",
    scene: "香港 · 天台",
    desc: "王静用半开玩笑的语气给向西讲成年人世界的规则。画面轻松，信息量却很大——这一场戏几乎决定了向西之后所有的行动方向。",
    image:
      "https://readdy.ai/api/search-image?query=Two%20silhouettes%20talking%20on%20a%20Hong%20Kong%20rooftop%20at%20dusk%2C%20city%20skyline%20with%20distant%20neon%20glow%2C%20warm%20amber%20and%20deep%20red%20sky%2C%20stylized%20cinematic%20film%20still%20with%20soft%20grain%20and%20bokeh&width=900&height=600&seq=clip-rooftop-02&orientation=landscape",
  },
  {
    id: "c3",
    tag: "overseas",
    tagLabel: "异乡求学",
    title: "英伦初到",
    scene: "英国 · 宿舍",
    desc: "镜头跟着向西走进陌生城市的日常。语言、天气与社交方式的差异同时涌来，他在新鲜与局促之间来回切换，为后来的情感线做足铺垫。",
    image:
      "https://readdy.ai/api/search-image?query=A%20rainy%20British%20university%20street%20at%20dusk%20with%20warm%20window%20lights%2C%20wet%20cobblestone%20reflections%2C%20stylized%20cinematic%20film%20still%2C%20amber%20and%20crimson%20tone%2C%20soft%20grain%20melancholic%20aesthetic&width=900&height=600&seq=clip-uk-03&orientation=landscape",
  },
  {
    id: "c4",
    tag: "overseas",
    tagLabel: "异乡求学",
    title: "没能说出口的告别",
    scene: "英国 · 车站",
    desc: "一段感情走到尽头，两个人却谁都没把话说清楚。影片在这里收起了惯常的调侃，用长镜头留出沉默，让观众自己体会那种无力感。",
    image:
      "https://readdy.ai/api/search-image?query=An%20empty%20train%20platform%20under%20warm%20evening%20lamps%2C%20lone%20figure%20standing%20still%2C%20stylized%20cinematic%20film%20still%2C%20deep%20crimson%20and%20amber%20color%20grade%2C%20soft%20grain%20and%20shallow%20depth%20of%20field&width=900&height=600&seq=clip-farewell-04&orientation=landscape",
  },
  {
    id: "c5",
    tag: "journey",
    tagLabel: "北上旅途",
    title: "出发那一夜",
    scene: "途中 · 夜间公路",
    desc: "好友几人凑在一起，把行李塞进车里，气氛热络又躁动。导演用一段轻快的剪辑，把少年人那种“终于要做点什么”的兴奋感推到位。",
    image:
      "https://readdy.ai/api/search-image?query=A%20car%20driving%20along%20a%20night%20highway%20toward%20distant%20city%20lights%2C%20warm%20amber%20and%20crimson%20motion%20blur%2C%20stylized%20cinematic%20film%20still%20with%20soft%20grain%20and%20bokeh%20tail%20lights&width=900&height=600&seq=clip-depart-05&orientation=landscape",
  },
  {
    id: "c6",
    tag: "journey",
    tagLabel: "北上旅途",
    title: "灯红酒绿的见闻",
    scene: "途中 · 夜场",
    desc: "旅途中最喧闹的一段。形形色色的人和事在镜头里轮番登场，热闹背后，影片悄悄给主角埋下一句疑问：这些就是他要找的东西吗？",
    image:
      "https://readdy.ai/api/search-image?query=A%20bustling%20night%20entertainment%20district%20with%20glowing%20signs%20and%20crowds%2C%20warm%20amber%20and%20deep%20red%20neon%2C%20stylized%20cinematic%20film%20still%20with%20motion%20blur%2C%20soft%20grain%20and%20bokeh%20lights&width=900&height=600&seq=clip-nightlife-06&orientation=landscape",
  },
  {
    id: "c7",
    tag: "reflect",
    tagLabel: "回望反思",
    title: "一个人的清晨",
    scene: "旅店 · 天亮",
    desc: "喧闹散尽，只剩向西独自坐在窗边。这一段几乎没有台词，却把全片的情绪压到最低点，也让观众第一次真正走进他的内心。",
    image:
      "https://readdy.ai/api/search-image?query=A%20quiet%20hotel%20room%20at%20dawn%20with%20soft%20morning%20light%20through%20thin%20curtains%2C%20lone%20figure%20sitting%20by%20the%20window%2C%20stylized%20cinematic%20film%20still%2C%20muted%20amber%20and%20crimson%20tone%2C%20soft%20grain&width=900&height=600&seq=clip-dawn-07&orientation=landscape",
  },
  {
    id: "c8",
    tag: "reflect",
    tagLabel: "回望反思",
    title: "回到起点的旁白",
    scene: "香港 · 收尾",
    desc: "旅程结束，向西的旁白重新响起。同样的口吻，却和开场时完全不同——这正是全片最巧妙的一处结构呼应，也是理解电影的关键。",
    image:
      "https://readdy.ai/api/search-image?query=A%20Hong%20Kong%20street%20corner%20returning%20to%20quiet%20after%20rain%20at%20night%2C%20reflections%20on%20the%20pavement%2C%20warm%20amber%20and%20deep%20crimson%20neon%20glow%2C%20stylized%20cinematic%20film%20still%2C%20soft%20grain&width=900&height=600&seq=clip-ending-08&orientation=landscape",
  },
];